import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import { getComparisonModeOptions } from "../_shared/comparisonPeriod";
import ComparisonPeriodControl from "../_shared/ComparisonPeriodControl";
import { validateThresholdConfig } from "./utils/thresholdUtils";
import DateRangePresets from "../../../features/filter/TabFilterModal/DateRangePresets";
import LocalFilterPanel from "../../../components/common/LocalFilterPanel";
import ReferenceLineSettings from "./ReferenceLineSettings";

const THRESHOLD_TYPE_OPTIONS = [
  { value: "", label: "None (disabled)" },
  { value: "more_the_better", label: "The more the better" },
  { value: "less_the_better", label: "The less the better" },
  { value: "optimal_range", label: "Optimal range" },
];

const GRANULARITY_OPTIONS = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const AttributeSumLineChartSettings = ({
  title,
  attributeSource,
  objectTypes,
  eventTypes,
  objectTypeNumericAttributes,
  eventTypeNumericAttributes,
  selectedObjectTypes,
  selectedObjectAttributes,
  selectedEventTypes,
  selectedEventAttributes,
  enabledGranularities,
  displayMode,
  valueAggregation,
  timestampAnchor,
  anchorActivity,
  activityOptions,
  comparisonEnabled,
  comparisonMode,
  limitComparisonToCurrentProgress,
  thresholdType,
  thresholdTarget,
  thresholdWarningPercentage,
  thresholdTargetPerDay,
  thresholdLowerThreshold,
  thresholdUpperThreshold,
  thresholdLowerThresholdPerDay,
  thresholdUpperThresholdPerDay,
  onTitleChange,
  onAttributeSourceChange,
  onSelectedObjectTypesChange,
  onSelectedObjectAttributesChange,
  onSelectedEventTypesChange,
  onSelectedEventAttributesChange,
  onEnabledGranularitiesChange,
  onDisplayModeChange,
  onValueAggregationChange,
  onTimestampAnchorChange,
  onAnchorActivityChange,
  onComparisonEnabledChange,
  onComparisonModeChange,
  onLimitComparisonToCurrentProgressChange,
  onThresholdTypeChange,
  onThresholdTargetChange,
  onThresholdWarningPercentageChange,
  onThresholdTargetPerDayChange,
  onThresholdLowerThresholdChange,
  onThresholdUpperThresholdChange,
  onThresholdLowerThresholdPerDayChange,
  onThresholdUpperThresholdPerDayChange,
  dateRangeStart,
  dateRangeEnd,
  onDateRangePresetChange,
  localFilters,
  updateLocalFilter,
  referenceLine,
  onReferenceLineChange,
  mainLineColor,
  onMainLineColorChange,
  activeFile,
  loading = false,
}) => {
  const { t } = useTranslation();
  const toggleGranularity = (id) => {
    if (enabledGranularities.includes(id)) {
      const next = enabledGranularities.filter((g) => g !== id);
      if (next.length > 0) {
        onEnabledGranularitiesChange(next);
      }
      return;
    }
    onEnabledGranularitiesChange([...enabledGranularities, id]);
  };

  const availableObjectAttributes = useMemo(() => {
    if (!selectedObjectTypes.length) return [];
    const attrSet = new Set();
    selectedObjectTypes.forEach((ot) => {
      const attrs = objectTypeNumericAttributes[ot] || [];
      attrs.forEach((a) => attrSet.add(a));
    });
    return Array.from(attrSet).sort();
  }, [selectedObjectTypes, objectTypeNumericAttributes]);

  const availableEventAttributes = useMemo(() => {
    if (!selectedEventTypes.length) return [];
    const attrSet = new Set();
    selectedEventTypes.forEach((et) => {
      const attrs = eventTypeNumericAttributes[et] || [];
      attrs.forEach((a) => attrSet.add(a));
    });
    return Array.from(attrSet).sort();
  }, [selectedEventTypes, eventTypeNumericAttributes]);

  const comparisonModeOptions = useMemo(() => getComparisonModeOptions(t), [t]);

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Attribute Sum Line Chart"
          disabled={loading}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Attribute Source
        </label>
        <div className="flex gap-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="attributeSourceLine"
              value="object"
              checked={attributeSource === "object"}
              onChange={() => onAttributeSourceChange("object")}
              className="focus:ring-primary-500"
              style={{ accentColor: "#0066cc" }}
              disabled={loading}
            />
            <span className="text-sm text-gray-700">Object Attribute</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="attributeSourceLine"
              value="event"
              checked={attributeSource === "event"}
              onChange={() => onAttributeSourceChange("event")}
              className="focus:ring-primary-500"
              style={{ accentColor: "#0066cc" }}
              disabled={loading}
            />
            <span className="text-sm text-gray-700">Event Attribute</span>
          </label>
        </div>
      </div>

      {attributeSource === "object" && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Object Configuration
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <MultiSelectDropdown
                label="Object Type *"
                options={(objectTypes || []).map(String)}
                selectedValues={selectedObjectTypes}
                onSelectionChange={onSelectedObjectTypesChange}
                placeholder="Select object types..."
                disabled={loading || objectTypes.length === 0}
                itemType="object types"
              />
            </div>
            <div>
              <MultiSelectDropdown
                label="Object Attribute *"
                options={availableObjectAttributes.map(String)}
                selectedValues={selectedObjectAttributes}
                onSelectionChange={onSelectedObjectAttributesChange}
                placeholder={
                  selectedObjectTypes.length === 0
                    ? "Select object types first..."
                    : availableObjectAttributes.length === 0
                      ? "No numeric attributes available"
                      : "Select attributes..."
                }
                disabled={loading || availableObjectAttributes.length === 0}
                itemType="attributes"
              />
            </div>
          </div>
        </div>
      )}

      {attributeSource === "event" && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Event Configuration
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <MultiSelectDropdown
                label="Event Type *"
                options={(eventTypes || []).map(String)}
                selectedValues={selectedEventTypes}
                onSelectionChange={onSelectedEventTypesChange}
                placeholder="Select event types..."
                disabled={loading || eventTypes.length === 0}
                itemType="event types"
              />
            </div>
            <div>
              <MultiSelectDropdown
                label="Event Attribute *"
                options={availableEventAttributes.map(String)}
                selectedValues={selectedEventAttributes}
                onSelectionChange={onSelectedEventAttributesChange}
                placeholder={
                  selectedEventTypes.length === 0
                    ? "Select event types first..."
                    : availableEventAttributes.length === 0
                      ? "No numeric attributes available"
                      : "Select attributes..."
                }
                disabled={loading || availableEventAttributes.length === 0}
                itemType="attributes"
              />
            </div>
          </div>
        </div>
      )}

      {/* Timestamp Placement (Object Attribute mode only) */}
      {attributeSource === "object" && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Timestamp Placement
          </label>
          <div className="flex gap-4 flex-wrap">
            {[
              { value: "last_event", label: "Last Event" },
              { value: "first_event", label: "First Event" },
              { value: "specific_activity", label: "Specific Activity" },
            ].map(({ value, label }) => (
              <label key={value} className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="timestampAnchorLine"
                  value={value}
                  checked={timestampAnchor === value}
                  onChange={() => onTimestampAnchorChange(value)}
                  className="focus:ring-primary-500"
                  style={{ accentColor: "#0066cc" }}
                  disabled={loading}
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>
          {timestampAnchor === "specific_activity" && (
            <div>
              <select
                value={anchorActivity || ""}
                onChange={(e) => onAnchorActivityChange(e.target.value || null)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={loading || activityOptions.length === 0}
              >
                <option value="">Select activity...</option>
                {(activityOptions || []).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Objects without this activity are excluded from the chart.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Comparison Period */}
      <ComparisonPeriodControl
        enabled={comparisonEnabled}
        onEnabledChange={onComparisonEnabledChange}
        mode={comparisonMode}
        onModeChange={onComparisonModeChange}
        modeOptions={comparisonModeOptions}
        limitToCurrentProgress={limitComparisonToCurrentProgress}
        onLimitToCurrentProgressChange={onLimitComparisonToCurrentProgressChange}
        loading={loading}
      />

      {/* Display Mode */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Display Mode
        </label>
        <div className="flex gap-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="displayModeLine"
              value="aggregated"
              checked={displayMode === "aggregated"}
              onChange={() => onDisplayModeChange("aggregated")}
              className="focus:ring-primary-500"
              style={{ accentColor: "#0066cc" }}
              disabled={loading}
            />
            <span className="text-sm text-gray-700">Aggregated per bucket</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="displayModeLine"
              value="cumulative"
              checked={displayMode === "cumulative"}
              onChange={() => onDisplayModeChange("cumulative")}
              className="focus:ring-primary-500"
              style={{ accentColor: "#0066cc" }}
              disabled={loading}
            />
            <span className="text-sm text-gray-700">
              Cumulative over time range
            </span>
          </label>
        </div>
      </div>

      {/* Main Line Color */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <label
          htmlFor="mainLineColorInput"
          className="block text-sm font-medium text-gray-700"
        >
          {t("tiles:attributeSum.mainLineColor")}
        </label>
        <div className="flex items-center gap-2">
          <input
            id="mainLineColorInput"
            type="color"
            value={mainLineColor || "#00a2ed"}
            onChange={(e) => onMainLineColorChange(e.target.value)}
            disabled={loading}
            className="w-9 h-9 rounded border border-gray-300 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          />
          <span className="text-sm text-gray-500 font-mono">
            {mainLineColor || "#00a2ed"}
          </span>
        </div>
      </div>

      {/* Y-Axis Aggregation */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Y-Axis Aggregation
        </label>
        <div className="flex gap-4">
          {[
            { value: "sum", label: "Sum" },
            { value: "average", label: "Average" },
            { value: "median", label: "Median" },
          ].map(({ value, label }) => (
            <label key={value} className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="valueAggregationLine"
                value={value}
                checked={valueAggregation === value}
                onChange={() => onValueAggregationChange(value)}
                className="focus:ring-primary-500"
                style={{ accentColor: "#0066cc" }}
                disabled={loading}
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Threshold Configuration */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Threshold
        </label>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Threshold Type
          </label>
          <select
            value={thresholdType || ""}
            onChange={(e) =>
              onThresholdTypeChange(e.target.value || null)
            }
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={loading}
          >
            {THRESHOLD_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Dynamic input fields based on thresholdType × displayMode */}
        {thresholdType &&
          thresholdType !== "optimal_range" &&
          displayMode !== "cumulative" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Target
                </label>
                <input
                  type="number"
                  value={thresholdTarget ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdTargetChange(v === "" ? null : Number(v));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 100"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Warning Percentage (%)
                </label>
                <input
                  type="number"
                  value={thresholdWarningPercentage ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdWarningPercentageChange(
                      v === "" ? null : Number(v),
                    );
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={
                    thresholdType === "more_the_better"
                      ? "e.g. 80"
                      : "e.g. 120"
                  }
                  disabled={loading}
                />
              </div>
            </div>
          )}

        {thresholdType &&
          thresholdType !== "optimal_range" &&
          displayMode === "cumulative" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Target per day
                </label>
                <input
                  type="number"
                  value={thresholdTargetPerDay ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdTargetPerDayChange(
                      v === "" ? null : Number(v),
                    );
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 10"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Warning Percentage (%)
                </label>
                <input
                  type="number"
                  value={thresholdWarningPercentage ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdWarningPercentageChange(
                      v === "" ? null : Number(v),
                    );
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={
                    thresholdType === "more_the_better"
                      ? "e.g. 80"
                      : "e.g. 120"
                  }
                  disabled={loading}
                />
              </div>
            </div>
          )}

        {thresholdType === "optimal_range" &&
          displayMode !== "cumulative" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Lower Threshold
                </label>
                <input
                  type="number"
                  value={thresholdLowerThreshold ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdLowerThresholdChange(
                      v === "" ? null : Number(v),
                    );
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 80"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Upper Threshold
                </label>
                <input
                  type="number"
                  value={thresholdUpperThreshold ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdUpperThresholdChange(
                      v === "" ? null : Number(v),
                    );
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 120"
                  disabled={loading}
                />
              </div>
            </div>
          )}

        {thresholdType === "optimal_range" &&
          displayMode === "cumulative" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Lower Threshold per day
                </label>
                <input
                  type="number"
                  value={thresholdLowerThresholdPerDay ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdLowerThresholdPerDayChange(
                      v === "" ? null : Number(v),
                    );
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 8"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Upper Threshold per day
                </label>
                <input
                  type="number"
                  value={thresholdUpperThresholdPerDay ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    onThresholdUpperThresholdPerDayChange(
                      v === "" ? null : Number(v),
                    );
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 12"
                  disabled={loading}
                />
              </div>
            </div>
          )}

        {/* Validation warning */}
        {thresholdType &&
          (() => {
            const warning = validateThresholdConfig(thresholdType, {
              target: thresholdTarget,
              warningPercentage: thresholdWarningPercentage,
              targetPerDay: thresholdTargetPerDay,
              lowerThreshold: thresholdLowerThreshold,
              upperThreshold: thresholdUpperThreshold,
              lowerThresholdPerDay: thresholdLowerThresholdPerDay,
              upperThresholdPerDay: thresholdUpperThresholdPerDay,
            });
            return warning ? (
              <p className="text-xs text-amber-600">{warning}</p>
            ) : null;
          })()}
      </div>

      {/* Average/Median Reference Line — fully independent data basis, own section */}
      <ReferenceLineSettings
        referenceLine={referenceLine}
        onChange={onReferenceLineChange}
        objectTypes={objectTypes}
        eventTypes={eventTypes}
        objectTypeNumericAttributes={objectTypeNumericAttributes}
        eventTypeNumericAttributes={eventTypeNumericAttributes}
        activeFile={activeFile}
        loading={loading}
      />

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Enabled Granularities (View Mode)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {GRANULARITY_OPTIONS.map((option) => {
            const active = enabledGranularities.includes(option.id);
            return (
              <label
                key={option.id}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                  active
                    ? "bg-primary-50 border-primary-300"
                    : "bg-white border-gray-300"
                } ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleGranularity(option.id)}
                  className="rounded border-gray-300 focus:ring-primary-500"
                  style={{ accentColor: "#0066cc" }}
                  disabled={loading}
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {onDateRangePresetChange && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Date Range (Date Range Mode)
          </label>
          <DateRangePresets
            value={{ startDate: dateRangeStart || null, endDate: dateRangeEnd || null, enabled: true }}
            onChange={onDateRangePresetChange}
          />
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Local Filter Panel - always visible */}
      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        enabledFilterTypes={['dateRange', 'activityNodes', 'objectAttributes']}
      />
    </div>
  );
};

export default AttributeSumLineChartSettings;
